import React, { useCallback, useState } from 'react';
import { Button, Divider, Typography } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { CountChatSessionResultDto, GetCallDetailsDto } from 'types';
import DashboardChart from './dashboard/DashboardChart';
import DashboardTable from './dashboard/DashboardTable';
import DashboardChatStats from './dashboard/DashboardChatStats';
import { useLazyGetOnlineAgentMapSeatsQuery, useLazyGetCallDetailsQuery, useLazyCountChatSessionQuery } from 'services';
import { OmniChannelLeadChatHistoriesModal } from 'components/omni-channel-chat-histories';
import { AIIcon2, RightIcon } from 'assets';
import { Link } from 'react-router-dom';
import { ROUTE } from 'routes/constants';
import { useProfile } from 'hooks';
import { checkPermissions } from 'utils/check-permission';
import { PERMISSION } from 'utils';
import DashboardAIAgent from './dashboard/DashboardAIAgent';

interface DashboardProps {
  callStatsBySource?: GetCallDetailsDto[];
  getChatSessionDetails?: CountChatSessionResultDto;
}

const Dashboard: React.FC<DashboardProps> = ({ callStatsBySource, getChatSessionDetails }) => {
  const [openHistoryModal, setOpenHistoryModal] = useState(false);

  const dispatch = useDispatch();
  const { userPermissionIds } = useProfile();

  const isAccessAIAgentCenter = checkPermissions(userPermissionIds, PERMISSION.MONITOR_AI_AGENT);

  const onlineAgentsForChart = useSelector((state: RootState) => state.agentMap.onlineDataForChart);
  const lastSocketChartUpdateTime = useSelector((state: RootState) => state.agentMap.lastSocketChartUpdateTime);

  // Use these lazy queries for refresh functionality
  const [fetchOnlineSeats] = useLazyGetOnlineAgentMapSeatsQuery();
  const [getCallDetailsTrigger] = useLazyGetCallDetailsQuery();
  const [getChatSessionDetailsTrigger] = useLazyCountChatSessionQuery();

  // Refresh all data function
  const refreshAllData = useCallback(() => {
    // console.log('📊 Dashboard: Refreshing all data at midnight...');

    // Get today's date range for chat stats
    const dateRange = (() => {
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      return {
        fromDate: startOfDay.toISOString(),
        toDate: endOfDay.toISOString()
      };
    })();

    // Refresh online agents data
    fetchOnlineSeats().then((response) => {
      if (response.data?.data) {
        dispatch({
          type: 'agentMap/setOnlineAgentMapData',
          payload: response.data.data
        });
      }
    });

    // Refresh call details
    getCallDetailsTrigger();

    // Refresh chat session details
    getChatSessionDetailsTrigger(dateRange);
  }, [dispatch, fetchOnlineSeats, getCallDetailsTrigger, getChatSessionDetailsTrigger]);

  return (
    <div className='rounded-lg bg-white p-4'>
      {/* Call Statistics Table */}
      <div className='mb-6 border-b'>
        <h2 className='mb-3 text-lg font-semibold'>Cuộc gọi hôm nay</h2>
        <DashboardTable callStatsBySource={callStatsBySource} />
      </div>

      {/* Agent Status Chart */}
      <div className='mb-6 border-b'>
        <DashboardChart
          onlineAgentsForChart={onlineAgentsForChart}
          lastSocketChartUpdateTime={lastSocketChartUpdateTime}
          refreshData={refreshAllData} // Pass the refresh function
        />
      </div>

      {/* Chat Statistics Section */}
      <div>
        <div className='flex items-center justify-between'>
          <Typography.Text className='text-lg font-semibold'>Chat hôm nay</Typography.Text>
          <Link to={ROUTE.CHAT_MANAGEMENT} className='underline underline-offset-2'>
            Quản lý
          </Link>
        </div>
        <DashboardChatStats getChatSessionDetails={getChatSessionDetails} />
        <OmniChannelLeadChatHistoriesModal onCancel={() => setOpenHistoryModal(false)} open={openHistoryModal} />
      </div>
      {isAccessAIAgentCenter && (
        <>
          <Divider className='mb-6 mt-5' />
          <div>
            <div className='flex items-center gap-2'>
              <AIIcon2 height={24} width={24} />
              <Typography.Text className='text-lg font-semibold'>ĐTV AI tiếp nhận hôm nay</Typography.Text>
              <Link to={ROUTE.AI_AGENT_MANAGEMENT} className='ml-auto'>
                <Typography.Link className='flex items-center gap-2'>
                  Quản lý <RightIcon />
                </Typography.Link>
              </Link>
            </div>
            <DashboardAIAgent />
          </div>
        </>
      )}
    </div>
  );
};

// Export with memo but allow re-rendering on updates
export default React.memo(Dashboard);
